import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, KanbanSquare, Building2, Calendar as CalendarIcon,
  AlertCircle, FileText, ChevronDown, BarChart3, FileSignature, Sparkles,
  Eye, Send, MessageCircle, Trophy, Settings, ChevronsLeft, ChevronsRight,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import { LOGOS, cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import AthleteSelector from "@/components/layout/AthleteSelector";
import { WorkspaceSwitcher } from "@/components/layout/WorkspaceSwitcher";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// NOTE: Dashboard keeps the EXACT existing four-cell grid icon (LayoutDashboard). Do not substitute.
const NAV_TOP = [
  { to: "/dashboard", label: "Dashboard", i18nKey: "nav.dashboard", icon: LayoutDashboard, testid: "side-nav-dashboard" },
  { to: "/calendar", label: "Calendrier", i18nKey: "nav.calendar", icon: CalendarIcon, testid: "side-nav-calendar" },
  { to: "/contracts", label: "Contrats", i18nKey: "nav.contracts", icon: FileSignature, testid: "side-nav-contracts" },
  { to: "/relances", label: tfr("À relancer"), i18nKey: "nav.relances", icon: AlertCircle, testid: "side-nav-relances", badge: "relances", urgent: true },
];

const GROUPS = [
  {
    id: "prospection", label: "Prospection",
    items: [
      { to: "/pipeline", label: "Vue pipeline", icon: KanbanSquare, testid: "side-nav-pipeline" },
      { to: "/reperes", label: tfr("Repérés"), icon: Eye, testid: "side-nav-reperes", badge: "reperes" },
      { to: "/prises-de-contact", label: "Prises de contact", icon: Send, testid: "side-nav-contacts", badge: "contacts" },
      { to: "/discussions-actives", label: "Discussions", icon: MessageCircle, testid: "side-nav-discussions-group" },
    ],
  },
  {
    id: "clients", label: "Clients",
    items: [
      { to: "/mandats-signes", label: tfr("Mandats signés"), icon: FileSignature, testid: "side-nav-mandats", badge: "mandats" },
      { to: "/placements", label: "Placements", icon: Trophy, testid: "side-nav-placements" },
    ],
  },
];

const GROUPS_AFTER_INTEL = [
  {
    id: "ressources", label: "Ressources",
    items: [
      { to: "/clubs", label: "Clubs", i18nKey: "nav.clubs", icon: Building2, testid: "side-nav-clubs" },
      { to: "/documents", label: "Documents", i18nKey: "nav.documents", icon: FileText, testid: "side-nav-documents", badge: "docs", urgent: true },
    ],
  },
];

const NAV_PERFORMANCE = { to: "/performance", label: "Performance", i18nKey: "nav.performance", icon: BarChart3, testid: "side-nav-performance" };

const DEFAULT_GROUPS = { prospection: true, clients: true, ressources: false };

const routeGroup = (pathname) => {
  for (const g of [...GROUPS, ...GROUPS_AFTER_INTEL]) {
    if (g.items.some((it) => it.to && (pathname === it.to || pathname.startsWith(it.to + "/")))) return g.id;
  }
  return null;
};

function loadGroups() {
  try {
    const raw = JSON.parse(localStorage.getItem("athlynx_nav_groups") || "{}");
    return { ...DEFAULT_GROUPS, ...raw };
  } catch { return { ...DEFAULT_GROUPS }; }
}

export function SidebarContent({ onNavigate, collapsed = false, onToggleCollapse, showSelector = false }) {
  const { theme } = useTheme();
  const { t, tf } = useLang();
  const { user } = useAuth();
  const { data: notifs } = useNotifications();
  const location = useLocation();
  const [groups, setGroups] = useState(loadGroups);

  const logoSrc = theme === "dark" ? LOGOS.white : LOGOS.black;
  const badges = {
    relances: notifs?.counts?.relances_overdue || 0,
    docs: (notifs?.counts?.docs_expired || 0) + (notifs?.counts?.docs_expiring_soon || 0),
    reperes: notifs?.status_totals?.["Repéré"] || 0,
    contacts: notifs?.status_totals?.["Contacté"] || 0,
    mandats: notifs?.status_totals?.["Mandat signé"] || 0,
  };
  const groupUrgent = (g) => g.items.reduce((s, it) => s + (it.urgent && it.badge ? badges[it.badge] || 0 : 0), 0);

  useEffect(() => {
    const g = routeGroup(location.pathname);
    if (g) setGroups((prev) => (prev[g] ? prev : { ...prev, [g]: true }));
  }, [location.pathname]);

  const toggleGroup = useCallback((id) => {
    setGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem("athlynx_nav_groups", JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  const renderItem = (item) => {
    const badge = item.badge ? badges[item.badge] : 0;
    const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
    const label = item.i18nKey ? t(item.i18nKey) : tf(item.label);
    const node = (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={onNavigate}
        data-testid={item.testid}
        aria-label={collapsed ? label : undefined}
        className={cn(
          "relative flex items-center gap-2.5 rounded-lg text-sm font-medium transition-colors duration-150",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          collapsed ? "justify-center h-10 w-10 mx-auto" : "h-10 px-3",
          active ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
        )}
      >
        {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[3px] rounded-full bg-athlynx" aria-hidden />}
        <item.icon className={cn("h-4 w-4 shrink-0", active && "text-foreground")} />
        {!collapsed && <span className="flex-1 truncate">{label}</span>}
        {!collapsed && badge > 0 && (
          <span
            className={cn(
              "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums leading-none",
              item.urgent ? "bg-danger text-white" : "bg-secondary border border-border text-muted-foreground"
            )}
            data-testid={`${item.testid}-badge`}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </NavLink>
    );
    return collapsed ? <Tip key={item.to} label={label}>{node}</Tip> : node;
  };

  const renderGroup = (g) => {
    if (collapsed) return g.items.map(renderItem);
    const open = !!groups[g.id];
    const urgent = groupUrgent(g);
    return (
      <div key={g.id} className="pt-4">
        <button
          onClick={() => toggleGroup(g.id)}
          aria-expanded={open}
          aria-controls={`nav-group-${g.id}`}
          data-testid={`side-group-${g.id}`}
          className="w-full flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 font-semibold hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
        >
          <span>{tf(g.label)}</span>
          {!open && urgent > 0 && <span className="h-1.5 w-1.5 rounded-full bg-athlynx shrink-0" data-testid={`side-group-${g.id}-urgent-dot`} aria-label="Attention requise" />}
          <span className="flex-1" />
          <ChevronDown className={cn("h-3 w-3 transition-transform duration-200 motion-reduce:transition-none", !open && "-rotate-90")} aria-hidden />
        </button>
        <div id={`nav-group-${g.id}`} hidden={!open} className="space-y-0.5 mt-0.5">
          {g.items.map(renderItem)}
        </div>
      </div>
    );
  };

  const intelActive = location.pathname === "/intelligence";
  const intelNode = (
    <NavLink
      to="/intelligence"
      onClick={onNavigate}
      data-testid="side-nav-intelligence"
      aria-label={collapsed ? "Athlynx Intelligence" : undefined}
      className={cn(
        "relative flex items-center gap-2.5 rounded-lg text-sm font-semibold transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-athlynx hover:bg-athlynx/5",
        collapsed ? "justify-center h-10 w-10 mx-auto" : "h-10 px-3",
        intelActive && "bg-athlynx/10"
      )}
    >
      {intelActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[3px] rounded-full bg-athlynx" aria-hidden />}
      <Sparkles className="h-4 w-4 shrink-0 text-athlynx" />
      {!collapsed && <span className="flex-1 truncate">Athlynx Intelligence</span>}
    </NavLink>
  );

  const settingsActive = location.pathname.startsWith("/settings");
  const settingsNode = (
    <NavLink
      to="/settings"
      onClick={onNavigate}
      data-testid="side-nav-settings"
      aria-label={collapsed ? t("nav.settings") : undefined}
      className={cn(
        "relative flex items-center gap-2.5 rounded-lg text-sm font-medium transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        collapsed ? "justify-center h-10 w-10 mx-auto" : "h-10 px-3",
        settingsActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
      )}
    >
      {settingsActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[3px] rounded-full bg-athlynx" aria-hidden />}
      <Settings className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="flex-1 truncate">{t("nav.settings")}</span>}
    </NavLink>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex flex-col h-full" data-testid="sidebar">
        {/* Brand + collapse control */}
        <div className={cn("flex items-center pt-5 pb-4", collapsed ? "flex-col gap-3 px-2" : "px-5 gap-2")}>
          <Link to="/dashboard" onClick={onNavigate} data-testid="sidebar-logo-link" className="min-w-0">
            <img src={logoSrc} alt="Athlynx" className={cn("w-auto", collapsed ? "h-6" : "h-7")} />
          </Link>
          {!collapsed && <span className="flex-1" />}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              aria-label={collapsed ? tf("Développer la barre latérale") : tf("Réduire la barre latérale")}
              data-testid="sidebar-collapse-btn"
              className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-secondary/60 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring shrink-0"
            >
              {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            </button>
          )}
        </div>

        <WorkspaceSwitcher collapsed={collapsed} />

        {/* Mobile drawer keeps the athlete/workspace selector (desktop moved it to the top bar) */}
        {showSelector && !collapsed && (
          <div className="px-4 pb-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-semibold px-1 mb-1.5">{t("sidebar.agent")}</div>
            <AthleteSelector onNavigate={onNavigate} />
          </div>
        )}

        <nav className={cn("space-y-0.5 overflow-y-auto overflow-x-hidden min-h-0 flex-1 scroll-thin", collapsed ? "px-2" : "px-3")} aria-label="Navigation principale">
          {NAV_TOP.map(renderItem)}
          {GROUPS.map(renderGroup)}
          <div className={cn("pt-4", collapsed && "pt-2")}>{intelNode}</div>
          {GROUPS_AFTER_INTEL.map(renderGroup)}
          <div className={cn("pt-4", collapsed && "pt-2")}>{renderItem(NAV_PERFORMANCE)}</div>
        </nav>

        {/* Bottom: settings + user card */}
        <div className={cn("pb-4 pt-3 border-t border-border/60 mt-2", collapsed ? "px-2 space-y-2" : "px-3 space-y-2")}>
          {settingsNode}
          {!collapsed && (
            <Link
              to="/billing"
              onClick={onNavigate}
              className="block rounded-xl border border-border bg-secondary/40 p-3 hover:bg-secondary/70 transition-colors group"
              data-testid="sidebar-plan-card"
            >
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-athlynx/15 border border-athlynx/30 flex items-center justify-center shrink-0">
                  <span className="text-athlynx text-xs font-black">{(user?.name || "A").trim().charAt(0).toUpperCase()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold truncate">{user?.name || "Athlynx Agent"}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {user?.subscription_status === "active"
                      ? ({ starter: "Plan Starter", pro: "Plan Pro", agency: "Plan Agency", enterprise: "Plan Enterprise" }[user?.plan_id] || "Plan Pro")
                      : user?.subscription_status === "trialing" ? tf("Essai gratuit")
                      : tf("Offre à activer")}
                  </div>
                </div>
                <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
              </div>
            </Link>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

function Tip({ label, children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" className="text-xs">{label}</TooltipContent>
    </Tooltip>
  );
}

export default function Sidebar({ collapsed, onToggleCollapse }) {
  return (
    <aside
      className={cn(
        "hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 border-r border-border bg-surface/60 backdrop-blur-sm transition-[width] duration-200 motion-reduce:transition-none",
        collapsed ? "w-[68px]" : "w-[232px]"
      )}
      data-testid="desktop-sidebar"
    >
      <SidebarContent collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
    </aside>
  );
}
